import type { FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import { useState } from 'react'
import { Bomb, Gem } from 'lucide-react'
import { cn } from '@/lib/utils'
import { range, shuffle } from '@/lib/random'
import { useSequence } from '@/hooks/use-sequence'
import { SceneBadge, SceneStat } from '@/components/landing/games/scene-ui'

type Tile = 'hidden' | 'gem' | 'mine' | 'gem-ghost' | 'mine-ghost'
type Status = 'playing' | 'cashed' | 'bust'

const SIZE = 25
const MINES = 3

const multiplierAfter = (safe: number): number => {
    let value = 0.99
    for (let i = 0; i < safe; i++) value *= (SIZE - i) / (SIZE - MINES - i)
    return value
}

const POSTER: Tile[] = range(SIZE).map(index => ([6, 8, 12, 16, 18].includes(index) ? 'gem' : 'hidden'))

const TILE_STYLES: Record<Tile, string> = {
    hidden: 'bg-[#1b1b21] shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_3px_0_rgba(0,0,0,0.45)]',
    gem: 'bg-ember/[0.13] ring-1 ring-inset ring-ember/50 shadow-[0_0_26px_-6px_rgba(255,91,31,0.75)]',
    mine: 'animate-shake bg-danger/15 ring-1 ring-inset ring-danger/60 shadow-[0_0_26px_-6px_rgba(255,61,94,0.7)]',
    'gem-ghost': 'bg-white/[0.025]',
    'mine-ghost': 'bg-white/[0.025]'
}

const MinesScene: FC<SceneProps> = ({ active }) => {
    const [tiles, setTiles] = useState<Tile[]>(POSTER)
    const [safe, setSafe] = useState(5)
    const [status, setStatus] = useState<Status>('playing')

    useSequence(active, async ({ wait }) => {
        const mines = new Set(shuffle(range(SIZE)).slice(0, MINES))
        const order = shuffle(range(SIZE))
        const target = 3 + Math.floor(Math.random() * 5)

        setTiles(range(SIZE).map(() => 'hidden'))
        setSafe(0)
        setStatus('playing')

        if (!(await wait(700))) return

        let found = 0

        for (const index of order) {
            if (mines.has(index)) {
                setTiles(previous => previous.map((tile, i) => (i === index ? 'mine' : mines.has(i) ? 'mine-ghost' : tile)))
                setStatus('bust')
                break
            }

            found += 1
            setTiles(previous => previous.map((tile, i) => (i === index ? 'gem' : tile)))
            setSafe(found)

            if (!(await wait(380))) return

            if (found >= target) {
                setTiles(previous => previous.map((tile, i) => (tile !== 'hidden' ? tile : mines.has(i) ? 'mine-ghost' : 'gem-ghost')))
                setStatus('cashed')
                break
            }
        }

        await wait(1900)
    })

    const multiplier = multiplierAfter(safe)

    return (
        <div className='absolute inset-0 flex items-center justify-center gap-7 px-6 pb-6 pt-14'>
            <div className='grid aspect-square h-full max-h-[226px] grid-cols-5 gap-1.5'>
                {tiles.map((tile, index) => (
                    <div key={index} className={cn('relative grid place-items-center rounded-[9px] transition-colors duration-300', TILE_STYLES[tile])}>
                        {(tile === 'gem' || tile === 'gem-ghost') && (
                            <Gem
                                aria-hidden='true'
                                strokeWidth={1.8}
                                className={cn('size-[48%] animate-in fade-in zoom-in-50 duration-300', tile === 'gem' ? 'text-ember' : 'text-steel/25')}
                            />
                        )}

                        {(tile === 'mine' || tile === 'mine-ghost') && (
                            <Bomb
                                aria-hidden='true'
                                strokeWidth={1.8}
                                className={cn('size-[48%] animate-in fade-in zoom-in-50 duration-300', tile === 'mine' ? 'text-danger' : 'text-steel/25')}
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className='hidden w-[120px] shrink-0 flex-col gap-4 sm:flex'>
                <SceneStat label='Mines' value={MINES} />
                <SceneStat label='Gems found' value={`${safe} of ${SIZE - MINES}`} />
                <SceneStat label='Multiplier' value={`×${multiplier.toFixed(2)}`} tone={status === 'bust' ? 'muted' : 'ember'} />

                <div>
                    {status === 'playing' && <SceneBadge tone='muted'>Next ×{multiplierAfter(safe + 1).toFixed(2)}</SceneBadge>}
                    {status === 'cashed' && <SceneBadge tone='ember'>Cashed out</SceneBadge>}
                    {status === 'bust' && <SceneBadge tone='danger'>Mine hit</SceneBadge>}
                </div>
            </div>
        </div>
    )
}

export default MinesScene
